import React from 'react';
import { Box, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { SeasonBadge } from './Badges';
import { displayTitle, thumbTarget } from './format';
import { UNLINKED_NOTE, unlinkedPieces, unlinkedRows } from './orderPlan';
import { C, MONO, RADIUS } from './tokens';
import { RestockV2Order, RestockV2ThumbTarget, RestockV2Unlinked } from './types';

// Magnus 2026-09-15: styles the engine wanted — SKU 3588 among them — leave the Korea order
// entirely. Without this section that work disappears silently. These rows are NOT selectable and
// NOT in the copied order: nobody can order them yet. The fix is a label, not a tick.
//
// WHAT PUTS A ROW HERE CHANGED ON 2026-09-18 (Magnus: 「tag doesn't matter, there is order links」).
// It is a missing 下單連結, never a missing 進貨店家 tag. A tagless style is ordered exactly like any
// other and stays up in the Korea groups under 沒有分店家 — it never reaches this section, and the
// wording no longer sends him off to fix a tag that was never the problem.
const UnlinkedRow: React.FC<{ row: RestockV2Unlinked; onImage: (target: RestockV2ThumbTarget) => void }> = ({
  row, onImage,
}) => (
  <Box
    component="article" data-unlinked={row.sku} data-unsourced={row.sku}
    sx={{
      bgcolor: C.card, border: '1px dashed', borderColor: C.line2, borderRadius: `${RADIUS}px`,
      p: 1.1, mb: 0.9,
    }}
  >
    <Box sx={{ display: 'flex', gap: 1.25 }}>
      <ProductThumb
        target={thumbTarget({ sku: row.sku, title: row.title, image_url: row.image_url ?? null })}
        width={64} height={85} radius={7} onOpen={onImage}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
          <SeasonBadge value={row.season} />
          <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{row.cat}</Typography>
        </Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3 }}>
          {displayTitle(row.title)}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center', mt: '2px' }}>
          <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{row.sku}</Typography>
          {typeof row.listed_days === 'number' && (
            <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>上架 {row.listed_days} 天</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 12, color: C.back, fontWeight: 700, mt: '3px' }}>{row.reason}</Typography>
        <Typography sx={{ fontSize: 11.5, color: C.ink3 }}>{row.why}</Typography>
      </Box>
      <Box aria-label={`要訂 ${row.order_qty_would_be} 件`} sx={{ textAlign: 'right', flex: 'none' }}>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>要訂</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1.1 }}>
          {row.order_qty_would_be}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>件</Typography>
      </Box>
    </Box>
  </Box>
);

const UnsourcedSection: React.FC<{
  order: RestockV2Order | undefined; onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ order, onImage }) => {
  const rows = unlinkedRows(order);
  if (!rows.length) return null;
  const pieces = unlinkedPieces(order);
  return (
    <Box component="section" aria-label={`沒有下單連結 ${pieces} 件`} sx={{ mt: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '2px', pb: 0.5 }}>
        <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 900, letterSpacing: '-.01em' }}>
          沒有下單連結
          <Box component="span" sx={{ color: C.ink3, fontWeight: 700, fontSize: 12, ml: '5px' }}>
            {rows.length} 款 · 訂不到
          </Box>
        </Typography>
        <Typography
          sx={{
            ml: 'auto', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.back,
            bgcolor: C.backWash, borderRadius: '99px', px: 1,
          }}
        >
          {pieces} 件
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: C.ink2, px: '2px', pb: 0.9 }}>{UNLINKED_NOTE}</Typography>
      {rows.map((row) => <UnlinkedRow key={row.sku} row={row} onImage={onImage} />)}
    </Box>
  );
};

export default UnsourcedSection;
